import { useState, useEffect, useCallback } from "react";
import styles from "./AdminForm.module.css";
import apiService from "../../../../services/api";
import { validateFile, createImagePreview, revokeImagePreview } from "../../../../utils/fileUpload";

export default function OrganizationForm({ item, onSubmit, onCancel }) {
    const [formData, setFormData] = useState({
        imageFile: null,
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [fileError, setFileError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (item) {
            setFormData({
                imageFile: null, // 기존 이미지는 URL로 표시
            });
            // 기존 이미지가 있다면 미리보기로 설정
            if (item.imageUrl || item.image) {
                setImagePreview(item.imageUrl || item.image);
            }
        }
    }, [item]);

    // 컴포넌트 언마운트 시 이미지 미리보기 정리
    useEffect(() => {
        return () => {
            if (imagePreview && imagePreview !== item?.imageUrl && imagePreview !== item?.image) {
                revokeImagePreview(imagePreview);
            }
        };
    }, [imagePreview, item?.imageUrl, item?.image]);

    const handleImageChange = useCallback((e) => {
        const file = e.target.files[0];
        if (!file) return;

        // 파일 유효성 검사
        const validation = validateFile(file);
        if (!validation.isValid) {
            setFileError(validation.errors.join(', '));
            return;
        }

        setFileError("");
        setFormData(prev => ({ ...prev, imageFile: file }));
        
        // 이미지 미리보기 생성
        const preview = createImagePreview(file);
        setImagePreview(preview);
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        // 중복 제출 방지
        if (isSubmitting) {
            console.log('Form submission already in progress, ignoring duplicate submit');
            return;
        }
        
        if (!formData.imageFile && !item) {
            alert('이미지를 선택해주세요.');
            return;
        }

        if (fileError) {
            alert('이미지 파일을 확인해주세요: ' + fileError);
            return;
        }

        setIsSubmitting(true);
        
        try {
            console.log('Submitting organization form data:', formData);
            console.log('Item:', item);
            
            // 조직도는 항상 업데이트 (ID가 없어도 서버에서 처리)
            const result = await apiService.updateOrganization(item?.id, formData);
            console.log('Update result:', result);
            
            alert("저장되었습니다.");
            onSubmit();
        } catch (error) {
            console.error('Form submission error:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack
            });
            alert(`저장에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
        } finally {
            setIsSubmitting(false);
        }
    }, [formData, item, onSubmit, fileError, isSubmitting]);

    const handleCancel = useCallback(() => {
        if (isSubmitting) return;
        onCancel();
    }, [onCancel, isSubmitting]);

    return (
        <form className={styles.formWrap} onSubmit={handleSubmit}>
            <h2 className={styles.title}>조직도 이미지 수정</h2>
            
            <label className={styles.label}>
                조직도 이미지
                <div className={styles.imageUpload}>
                    <input
                        id="imageInput"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={isSubmitting}
                    />
                    <div className={styles.imageUploadIcon}>📷</div>
                    <div className={styles.imageUploadText}>이미지를 선택하세요</div>
                    <div className={styles.imageUploadHint}>JPG, PNG, GIF, SVG 파일 지원</div>
                </div>
                {fileError && <div className={styles.error}>{fileError}</div>}
                {imagePreview && (
                    <div className={styles.imagePreview}>
                        <img 
                            src={imagePreview} 
                            alt="조직도 미리보기" 
                        />
                    </div>
                )}
            </label>
            
            <div className={styles.buttonGroup}>
                <button 
                    type="submit" 
                    className={styles.submitBtn}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? "처리중..." : "저장"}
                </button>
                <button 
                    type="button" 
                    onClick={handleCancel} 
                    className={styles.cancelBtn}
                    disabled={isSubmitting}
                >
                    취소
                </button>
            </div>
        </form>
    );
}

